// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Modal, TextArea } from '@carbon/react';
import { ArrowLeft, Checkmark, WarningAltFilled } from '@carbon/icons-react';
import { useAlarm } from '../../hooks/useAlarms';
import { acknowledgeAlarm } from '../../services/alarmsService';
import { useShellStore } from '../../stores/shellStore';
import { SITE_LABELS } from '../../mocks/alarms';
import { tempBand } from '../../mocks/floorTemps';
import { SevTag, Loading, DxTag } from '../../components/shell/ScreenHelpers';
import AlarmTelemetry from '../../components/shell/AlarmTelemetry';
import type { FlowNode, FloorCell } from '../../types/alarm';
import './AlarmDetail.css';

function statusColor(s: FlowNode['status']) {
  return s === 'fault'
    ? 'var(--st-fault)'
    : s === 'warn'
      ? 'var(--st-warn)'
      : s === 'idle'
        ? 'var(--st-idle)'
        : 'var(--st-ok)';
}

function FloorMini({ cells }: { cells: FloorCell[] }) {
  return (
    <div className="ad-floor">
      {cells.map(c => {
        const band = tempBand(c.temp);
        return (
          <div
            key={c.id}
            className="ad-floor-cell"
            style={{ background: `var(--${band}bg)`, borderColor: `var(--${band}bd)` }}
          >
            <div className="ad-floor-id">{c.id}</div>
            <div className="ad-floor-val" style={{ color: `var(--${band})` }}>
              {c.val}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AlarmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alarm, loading, refetch } = useAlarm(id);
  const openAskHelios = useShellStore(s => s.openAskHelios);
  const [done, setDone] = useState<boolean[]>([]);
  const [ackOpen, setAckOpen] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (alarm) setDone(alarm.steps.map(() => false));
  }, [alarm]);

  if (loading) return <Loading />;
  if (!alarm) {
    return (
      <div className="hs-page">
        <p>Alarm not found.</p>
        <Button kind="ghost" onClick={() => navigate('/home/critical')}>
          Back to alarms
        </Button>
      </div>
    );
  }

  const handleAck = async () => {
    await acknowledgeAlarm(alarm.id);
    setAckOpen(false);
    refetch();
  };

  const meta: [string, string | undefined][] = [
    ['Event ID', alarm.ruleId ?? alarm.id],
    ['Time', alarm.time],
    ['Location', alarm.location],
    ['Asset ID', alarm.assetId],
    ['Source', alarm.src],
    ['Data quality', alarm.quality],
  ];

  return (
    <div className="hs-page ad-page">
      <button type="button" className="ad-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="ad-head">
        <div>
          <div className="hs-card-row" style={{ marginBottom: 8 }}>
            <SevTag sev={alarm.sev} />
            {alarm.statusChip && <DxTag>{alarm.statusChip}</DxTag>}
            {alarm.siteId && (
              <span className="ad-site">{SITE_LABELS[alarm.siteId] ?? alarm.siteId}</span>
            )}
          </div>
          <h1 className="hs-title">{alarm.asset}</h1>
          <p className="ad-issue">{alarm.issue}</p>
        </div>
        {alarm.needsAck && !alarm.closed && (
          <Button renderIcon={Checkmark} onClick={() => setAckOpen(true)}>
            Acknowledge
          </Button>
        )}
      </div>

      <div className="ad-grid">
        <div className="ad-col-main">
          {/* Metadata */}
          <div className="ad-metagrid">
            {meta.map(([k, v]) => (
              <div key={k} className="ad-metaitem">
                <div className="ad-metakey">{k}</div>
                <div className="ad-metaval">{v ?? '—'}</div>
              </div>
            ))}
          </div>

          {/* Helios AI card */}
          <section className="ad-card ad-ai">
            <div className="ad-card-title">
              <span className="ad-ai-mark">✦ Helios AI</span>
              <span className="ad-conf">Confidence · {alarm.confidence}</span>
            </div>
            <p className="ad-diag">{alarm.diagnosis}</p>
            {alarm.causes && (
              <ul className="ad-causes">
                {alarm.causes.map(c => (
                  <li key={c.c}>
                    <span className="ad-cause-rank">{c.rank}</span>
                    <span className="ad-cause-name">{c.c}</span>
                    <span className="ad-cause-conf">{c.conf}</span>
                    <span className="ad-cause-why">{c.why}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="ad-ai-actions">
              <Button
                size="sm"
                kind="tertiary"
                onClick={() =>
                  openAskHelios({
                    id: alarm.id,
                    rule: alarm.ruleId,
                    asset: alarm.assetId ?? alarm.asset,
                    site: alarm.siteLabel ?? SITE_LABELS[alarm.siteId] ?? alarm.siteId,
                    ranked_causes: alarm.causes?.map(c => c.c) ?? [],
                    bound_sop: alarm.sopId,
                    triage_summary: alarm.diagnosis,
                  })
                }
              >
                Ask Helios about this
              </Button>
              <span className="ad-disclaimer">Helios AI can make mistakes.</span>
            </div>
          </section>

          {/* Response procedure */}
          <section className="ad-card">
            <div className="ad-card-title">
              Response procedure
              <span className="ad-sop-id">
                {alarm.sopId ? <Link to={`/sops/${alarm.sopId}`}>{alarm.sopId}</Link> : alarm.sopId}
                {alarm.sopVersion ? ` · ${alarm.sopVersion}` : ''}
                {alarm.labor ? ` · ${alarm.labor}` : ''}
              </span>
            </div>
            <ol className="ad-steps">
              {alarm.steps.map((s, i) => (
                <li key={i} className={done[i] ? 'checked' : ''}>
                  <button
                    type="button"
                    className="ad-step-check"
                    aria-pressed={done[i]}
                    disabled={alarm.closed}
                    onClick={() => setDone(d => d.map((v, idx) => (idx === i ? !v : v)))}
                  >
                    {done[i] ? <Checkmark size={16} /> : null}
                  </button>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Telemetry — past-24h equipment observations (renders nothing if none) */}
          <AlarmTelemetry siteId={alarm.siteId} equipmentId={alarm.assetId} orgId={alarm.orgId} />

          {/* Audit trail */}
          {alarm.auditTrail.length > 0 && (
            <section className="ad-card">
              <div className="ad-card-title">Audit trail</div>
              <ul className="ad-trail">
                {alarm.auditTrail.map((e, i) => (
                  <li key={i}>
                    <span className="ad-trail-t">{e.t}</span>
                    <span className="ad-trail-who">{e.who}</span>
                    <span>{e.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="ad-col-side">
          {/* Asset map */}
          {(alarm.upstream?.length || alarm.downstream?.length) && (
            <section className="ad-card">
              <div className="ad-card-title">Asset map</div>
              <div className="ad-flow">
                {alarm.upstream?.map(n => (
                  <div key={`u-${n.label}`} className="ad-flow-node">
                    <span className="dot" style={{ background: statusColor(n.status) }} />
                    {n.label}
                  </div>
                ))}
                <div className="ad-flow-node focus">
                  <span className="dot" style={{ background: 'var(--helios)' }} />
                  {alarm.focusLabel ?? alarm.asset}
                </div>
                {alarm.downstream?.map(n => (
                  <div key={`d-${n.label}`} className="ad-flow-node">
                    <span className="dot" style={{ background: statusColor(n.status) }} />
                    {n.label}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Floor mini */}
          {alarm.floorCells && alarm.floorCells.length > 0 && (
            <section className="ad-card">
              <div className="ad-card-title">
                {alarm.floorTitle}
                {alarm.floorSub && <span className="ad-sop-id">{alarm.floorSub}</span>}
              </div>
              <FloorMini cells={alarm.floorCells} />
              {alarm.floorNote && <p className="ad-floor-note">{alarm.floorNote}</p>}
            </section>
          )}

          {/* Linked alarm */}
          {alarm.related && (
            <section className="ad-card">
              <div className="ad-card-title">Linked alarm</div>
              <div className="hs-card-row">
                <SevTag sev={alarm.related.sev} />
                <span style={{ fontSize: 13 }}>{alarm.related.title}</span>
              </div>
              <p className="ad-floor-note">{alarm.related.note}</p>
            </section>
          )}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="ad-actionbar">
        {alarm.closed ? (
          <span className="ad-actionbar-note">
            <Checkmark size={16} /> Cleared by Ignition telemetry · {alarm.outcome}
          </span>
        ) : (
          <>
            <span className="ad-actionbar-note">
              <WarningAltFilled size={16} /> Ignition owns clearance · acknowledge to take ownership
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button kind="tertiary" size="md">
                Raise work order
              </Button>
              {alarm.needsAck && (
                <Button size="md" onClick={() => setAckOpen(true)}>
                  Acknowledge
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        open={ackOpen}
        modalHeading={`Acknowledge ${alarm.id}`}
        primaryButtonText="Acknowledge"
        secondaryButtonText="Cancel"
        onRequestClose={() => setAckOpen(false)}
        onRequestSubmit={handleAck}
      >
        <p style={{ marginBottom: 16 }}>
          You are taking ownership of <strong>{alarm.asset}</strong> ({alarm.sev}). Clearance still
          happens in Ignition when telemetry recovers.
        </p>
        <TextArea
          id="ack-note"
          labelText="Note (optional)"
          placeholder="What you're doing / delegating…"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </Modal>
    </div>
  );
}
